import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Download, Zap, Store, Search, SlidersHorizontal, X } from "lucide-react";
import { exportCSV } from "@/lib/exports";
import usePageTitle from "@/hooks/use-page-title";
import { fmtDate } from "@/lib/date";

const SOURCE_LABELS = { all: "All", online: "Online", offline: "Offline" };

export default function Payments() {
  usePageTitle("Payments");
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("all");

  // Advanced filters
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [mode, setMode] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const advancedActive = !!(q || dateFrom || dateTo || mode || amountMin || amountMax);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    api.get("/payments").then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  }, []);

  const modes = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.mode) s.add(String(r.mode).toLowerCase()); });
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const amin = amountMin === "" ? null : Number(amountMin);
    const amax = amountMax === "" ? null : Number(amountMax);
    return rows.filter((r) => {
      if (source !== "all" && r.source !== source) return false;
      if (qq) {
        const hay = [r.customer_name, r.order_id, r.order_no, r.shop_order_no, r.invoice_no, r.razorpay_payment_id]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      const d = (r.created_at || r.payment_date || "").slice(0, 10);
      if (dateFrom && d && d < dateFrom) return false;
      if (dateTo && d && d > dateTo) return false;
      if (mode && String(r.mode || "").toLowerCase() !== mode) return false;
      const amt = Number(r.amount || 0);
      if (amin !== null && !Number.isNaN(amin) && amt < amin) return false;
      if (amax !== null && !Number.isNaN(amax) && amt > amax) return false;
      return true;
    });
  }, [rows, source, q, dateFrom, dateTo, mode, amountMin, amountMax]);

  const total = filtered.reduce((s, r) => s + (r.amount || 0), 0);
  const onlineCount = rows.filter((r) => r.source === "online").length;
  const offlineCount = rows.filter((r) => r.source === "offline").length;

  const clearAdvanced = () => {
    setQ(""); setDateFrom(""); setDateTo(""); setMode(""); setAmountMin(""); setAmountMax("");
  };

  return (
    <div className="space-y-6" data-testid="payments-root">
      <div className="border-b border-[hsl(var(--border))] pb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Cash Book</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Payments</h1>
          <div className="mt-2 text-xs text-muted-foreground">
            <span data-testid="payments-online-count"><b>{onlineCount}</b> online</span>
            {" · "}
            <span data-testid="payments-offline-count"><b>{offlineCount}</b> offline</span>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="inline-flex rounded-full bg-zinc-100 p-1 text-xs" data-testid="payments-source-tabs">
            {Object.entries(SOURCE_LABELS).map(([k, v]) => (
              <button
                key={k}
                onClick={() => setSource(k)}
                className={`px-3 py-1.5 rounded-full font-bold uppercase tracking-wider transition ${source === k ? "bg-white shadow text-[#FF6B00]" : "text-zinc-600 hover:text-zinc-900"}`}
                data-testid={`payments-tab-${k}`}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="text-right">
            <div className="overline">Total Received</div>
            <div className="font-display font-black text-3xl" data-testid="payments-total">
              ₹{Math.round(total).toLocaleString("en-IN")}
            </div>
          </div>
          <button
            onClick={() => exportCSV(`payments-${source}`, filtered.map((p) => ({
              Date: p.created_at || p.payment_date, Source: p.source, Customer: p.customer_name,
              OrderID: p.order_id, InvoiceNo: p.invoice_no || "", Mode: p.mode, Amount: p.amount,
              RazorpayId: p.razorpay_payment_id || "", Note: p.note,
            })))}
            className="btn-swiss btn-outline"
            data-testid="payments-export-csv"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 flex-1 min-w-[280px] max-w-md">
          <Search size={16} className="text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by customer, order ID, invoice no…"
            className="flex-1 bg-transparent px-3 py-2 text-sm outline-none"
            data-testid="payments-search-input"
          />
        </div>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className={`btn-swiss btn-outline ${advancedActive ? "border-[#FF6B00] text-[#FF6B00]" : ""}`}
          data-testid="payments-advanced-toggle"
          title="Filter by date, mode or amount range"
        >
          <SlidersHorizontal size={14} /> {showAdvanced ? "Hide filters" : "More filters"}
          {advancedActive && <span className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#FF6B00] text-white text-[9px] font-bold">•</span>}
        </button>
        {advancedActive && (
          <button
            type="button"
            onClick={clearAdvanced}
            className="btn-swiss btn-outline text-xs"
            data-testid="payments-clear-btn"
          >
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {showAdvanced && (
        <div className="card-swiss p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 items-end" data-testid="payments-advanced-panel">
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">From date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
              data-testid="payments-filter-date-from"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">To date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
              data-testid="payments-filter-date-to"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Mode</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
              data-testid="payments-filter-mode"
            >
              <option value="">All modes</option>
              {modes.map((m) => (
                <option key={m} value={m}>{m.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Amount min (₹)</label>
            <input
              type="number"
              min="0"
              placeholder="0"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
              data-testid="payments-filter-amount-min"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Amount max (₹)</label>
            <input
              type="number"
              min="0"
              placeholder="Any"
              value={amountMax}
              onChange={(e) => setAmountMax(e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
              data-testid="payments-filter-amount-max"
            />
          </div>
        </div>
      )}

      <div className="card-swiss overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-4 py-3 text-left overline">Date</th>
              <th className="px-4 py-3 text-left overline">Source</th>
              <th className="px-4 py-3 text-left overline">Customer</th>
              <th className="px-4 py-3 text-left overline">Order</th>
              <th className="px-4 py-3 text-left overline">Mode</th>
              <th className="px-4 py-3 text-right overline">Amount</th>
              <th className="px-4 py-3 text-left overline">Note</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const isOnline = p.source === "online";
              return (
                <tr
                  key={p.id}
                  className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                  data-testid={`payment-row-${p.id}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {fmtDate(p.created_at || p.payment_date)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isOnline ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-zinc-100 text-zinc-700 border border-zinc-200"}`} data-testid={`payment-source-${p.id}`}>
                      {isOnline ? <><Zap size={10} /> Online</> : <><Store size={10} /> Offline</>}
                    </span>
                  </td>
                  <td className="px-4 py-3">{p.customer_name || "—"}</td>
                  <td className="px-4 py-3">
                    {isOnline ? (
                      <span className="font-mono text-xs" title={p.razorpay_payment_id}>
                        {p.order_no || p.shop_order_no || p.order_id?.slice(0, 8)}
                      </span>
                    ) : (
                      <Link to={`/orders/${p.order_id}`} className="font-mono text-xs hover:text-[#FF6B00]">
                        {p.order_id}
                      </Link>
                    )}
                  </td>
                  <td className="px-4 py-3 uppercase text-xs">{p.mode}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">
                    ₹{Math.round(p.amount).toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{p.note || "—"}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                  No payments match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
